/**
 * newsletter-popup.js — Site-wide subscribe popup controller
 * Task: K-092
 *
 * Loaded via <script src="..." defer> in layout/theme.liquid.
 * Reads trigger config from data-* attributes on the <dialog> element.
 *
 * Trigger modes (data-trigger on dialog):
 *   "time_delay"   — show after data-delay seconds
 *   "exit_intent"  — show when mouse leaves top edge of viewport
 *   "scroll_50"    — show after 50% page scroll
 *
 * localStorage keys:
 *   kissa_newsletter_popup_last_shown     — timestamp of last show (not used for gating, kept for debugging)
 *   kissa_newsletter_popup_dismissed_until — timestamp; popup hidden until this time
 *
 * Integrations:
 *   - Listens for 'cart:updated' (dispatched by cart.js) — if cart has items, suppress popup
 *     to avoid interrupting active purchase flow.
 *   - focus trap + return: native <dialog>.showModal() provides focus trap and ESC.
 *     We additionally store the focused element before open and return focus on close.
 */

(function () {
  'use strict';

  var dialog = document.getElementById('newsletter-popup');
  if (!dialog) return;

  var trigger = dialog.dataset.trigger || 'time_delay';
  var delay = parseInt(dialog.dataset.delay, 10) || 15;
  var dismissDays = parseInt(dialog.dataset.dismissDays, 10) || 30;

  var DISMISSED_KEY = 'kissa_newsletter_popup_dismissed_until'; /* localStorage — permanent suppression, set on successful subscribe */
  var SESSION_DISMISS_KEY = 'kissa_newsletter_popup_dismissed_session'; /* sessionStorage — per-session suppression after a manual dismiss */
  var LAST_SHOWN_KEY = 'kissa_newsletter_popup_last_shown';

  var triggered = false;
  var cartHasItems = false;
  var previousFocus = null;

  /* ── Dismissal guard ──────────────────────────────────────────────────── */

  function isDismissed() {
    /* Permanent suppression for subscribers (localStorage — survives sessions) */
    var until = parseInt(localStorage.getItem(DISMISSED_KEY), 10);
    if (until && Date.now() < until) return true;
    /* Per-session suppression after a manual dismiss (sessionStorage — cleared
       when the tab/session ends, so a returning visitor sees it again). */
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY)) return true;
    } catch (e) {}
    return false;
  }

  function recordDismissed() {
    /* A dismiss (✕ / ESC / backdrop) hides the popup for the CURRENT SESSION
       only — next session it shows again. Subscribers stay suppressed long-term
       via DISMISSED_KEY (localStorage), set on the successful-submit branch. */
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch (e) {}
  }

  function recordLastShown() {
    localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
  }

  /* ── Focus helpers ────────────────────────────────────────────────────── */

  function firstFocusable(container) {
    return container.querySelector(
      'input:not([disabled]):not([type="hidden"]):not([aria-hidden="true"]), button:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  /* ── Open / close ─────────────────────────────────────────────────────── */

  function openPopup() {
    if (triggered) return;
    if (isDismissed()) return;
    if (cartHasItems) return;
    if (dialog.open) return;

    triggered = true;
    previousFocus = document.activeElement;

    dialog.showModal();
    recordLastShown();

    /* Focus the email input (primary action), NOT the close button: the ✕
       precedes the form in DOM order, so firstFocusable() returns it and its
       red brand-primary :focus-visible ring would flash on the ✕ as we open. */
    var target = dialog.querySelector('.newsletter-popup__email-input') || firstFocusable(dialog);
    if (target) target.focus();
  }

  function closePopup() {
    if (!dialog.open) return;
    dialog.close();
    recordDismissed();

    /* Return focus to where it was before the popup opened */
    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus();
    }
    previousFocus = null;
  }

  /* ── Close button click ───────────────────────────────────────────────── */

  var closeBtn = dialog.querySelector('[data-newsletter-popup-close]');
  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      closePopup();
    });
  }

  /* ── Backdrop click ───────────────────────────────────────────────────── */

  dialog.addEventListener('click', function (e) {
    /* The target is the <dialog> itself (not inner content) when clicking the backdrop */
    if (e.target === dialog) {
      closePopup();
    }
  });

  /* ── ESC / native close event ─────────────────────────────────────────── */

  dialog.addEventListener('close', function () {
    /* Dialog may have been closed by ESC (native) — record dismissal and return focus */
    recordDismissed();
    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus();
    }
    previousFocus = null;
  });

  /* ── Cart:updated — suppress popup while cart has items ──────────────── */

  document.addEventListener('cart:updated', function (e) {
    var cart = e.detail && e.detail.cart;
    cartHasItems = !!(cart && cart.item_count && cart.item_count > 0);
  });

  /* On page load, check current cart state via /cart.js (non-blocking) */
  fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then(function (res) { return res.json(); })
    .then(function (cart) {
      cartHasItems = !!(cart && cart.item_count && cart.item_count > 0);
    })
    .catch(function () {
      /* Non-fatal — popup may show even with items if cart fetch fails */
    });

  /* ── Trigger logic ───────────────────────────────────────────────────── */

  /* Bail immediately if dismissed */
  if (isDismissed()) return;

  if (trigger === 'time_delay') {
    /* Show after N seconds */
    setTimeout(openPopup, delay * 1000);

  } else if (trigger === 'exit_intent') {
    /* Show when mouse moves above the top edge of the viewport */
    function onMouseLeave(e) {
      if (e.clientY < 0) {
        openPopup();
        /* Remove listener once triggered */
        document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      }
    }
    document.documentElement.addEventListener('mouseleave', onMouseLeave);

  } else if (trigger === 'scroll_50') {
    /* Show after 50% of page scroll depth */
    function onScroll() {
      var scrolled = window.scrollY;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      var pct = scrolled / docHeight;
      if (pct >= 0.5) {
        openPopup();
        window.removeEventListener('scroll', onScroll, { passive: true });
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

})();
